/**
 * Promotional module countdown
 * ============================
 * Liquid owns correctness: it only renders the panel while the end date is in the future, with the
 * pre-countdown CTA. This module is for the visitor already on the page when the clock runs out — it
 * ticks down, swaps the CTA to the post-countdown pair, then retires the panel, landing on the same
 * state a fresh load would have produced.
 *
 * Every tick recomputes from an absolute instant rather than decrementing, so a throttled background
 * tab can delay a repaint but never accumulate drift.
 */

const UNITS = ['days', 'hours', 'minutes'];
const TICK_MS = 1000;
const ZERO_HOLD_MS = 1500;
// Two-stage exit: opacity fades for 350ms, then max-height/margin collapse for another 350ms.
const FADE_MS = 700;
// Past this the device clock is wrong enough to visibly break the timer, so the server wins. Below
// it a gap is more likely cached HTML than a bad clock, and correcting would spoil an accurate one.
const GROSS_SKEW_MS = 5 * 60 * 1000;
const EXPIRY_MESSAGE = 'The countdown has ended.';

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

// Returns null when the markup can't be trusted, so the caller tracks no instance that never ticks.
function setupInstance(root) {
  const section = root.closest('.promotional-module');
  const cta = section && section.querySelector('[data-promotional-cta]');
  const status = root.querySelector('[data-countdown-status]');
  const announcer = root.querySelector('[data-countdown-announcer]');

  const endMs = Number(root.dataset.countdownEnd) * TICK_MS;
  // Liquid emits 0 and skips the panel for a bad date, so this means hand-edited markup.
  if (!Number.isFinite(endMs) || endMs <= 0) {
    return null;
  }

  const serverNowMs = Number(root.dataset.countdownNow) * TICK_MS;
  const measuredSkew = Number.isFinite(serverNowMs) && serverNowMs > 0 ? serverNowMs - Date.now() : 0;
  const skewMs = Math.abs(measuredSkew) > GROSS_SKEW_MS ? measuredSkew : 0;
  const now = () => Date.now() + skewMs;

  // The rendered tile count is authoritative — days past 99 get a third tile.
  const groups = new Map();
  UNITS.forEach((unit) => {
    const group = root.querySelector(`[data-countdown-unit="${unit}"]`);
    if (group) {
      groups.set(unit, Array.from(group.querySelectorAll('[data-countdown-digit]')));
    }
  });

  const retireTimers = [];
  let timer = null;
  let expired = false;
  let lastStatus = '';

  function stop() {
    if (!timer) {
      return;
    }
    clearInterval(timer);
    timer = null;
  }

  function writeUnit(unit, value) {
    const digits = groups.get(unit);
    if (!digits) {
      return;
    }
    const text = String(value).padStart(digits.length, '0').slice(-digits.length);
    digits.forEach((digit, index) => {
      const glyph = digit.firstElementChild || digit;
      const next = text.charAt(index);
      // At minutes granularity 59 of every 60 ticks change nothing.
      if (glyph.textContent !== next) {
        glyph.textContent = next;
      }
    });
  }

  function expire() {
    if (expired) {
      return;
    }
    expired = true;
    stop();
    UNITS.forEach((unit) => writeUnit(unit, 0));

    if (cta) {
      const label = cta.dataset.ctaPostLabel;
      const url = cta.dataset.ctaPostUrl;
      if (label) {
        cta.textContent = label;
      }
      if (url) {
        cta.setAttribute('href', url);
      }
      // Only set when Liquid parked a post-only CTA because no pre-countdown label existed.
      cta.removeAttribute('hidden');
    }

    if (status) {
      status.textContent = EXPIRY_MESSAGE;
    }
    if (announcer) {
      announcer.textContent = EXPIRY_MESSAGE;
    }

    // Hold the zeros so completion is visible, then converge on what a fresh load renders.
    retireTimers.push(
      setTimeout(() => {
        root.classList.add('is-expired');
      }, ZERO_HOLD_MS)
    );
    retireTimers.push(
      setTimeout(
        () => {
          root.remove();
        },
        reducedMotion.matches ? ZERO_HOLD_MS : ZERO_HOLD_MS + FADE_MS
      )
    );
  }

  function tick() {
    const remainingMs = endMs - now();
    if (remainingMs <= 0) {
      expire();
      return;
    }

    const remaining = Math.floor(remainingMs / TICK_MS);
    const days = Math.floor(remaining / 86400);
    const hours = Math.floor((remaining % 86400) / 3600);
    // Hold at 1 for the final sub-minute so the display doesn't drop to 0 while time is still running.
    const minutes = Math.max(1, Math.floor((remaining % 3600) / 60));

    writeUnit('days', days);
    writeUnit('hours', hours);
    writeUnit('minutes', minutes);

    // aria-live is off, so refreshing keeps it accurate for anyone querying it without announcing.
    const phrase = `${days} days, ${hours} hours, ${minutes} minutes remaining`;
    if (status && phrase !== lastStatus) {
      lastStatus = phrase;
      status.textContent = phrase;
    }
  }

  function start() {
    stop();
    if (expired) {
      return;
    }
    // A one-second heartbeat despite the minute granularity: it bounds expiry precision to a second
    // and costs three subtractions, since every value is recomputed from `endMs`.
    timer = setInterval(tick, TICK_MS);
  }

  // Background tabs throttle intervals to minutes in some browsers. Values self-heal because they
  // are absolute, but repaint on return so the tiles are never visibly stale.
  function resync() {
    if (document.hidden) {
      return;
    }
    tick();
    start();
  }

  document.addEventListener('visibilitychange', resync);
  window.addEventListener('pageshow', resync);

  // A cached page can be served after the end date; ticking first expires it without a wrong second.
  tick();
  start();

  return {
    teardown() {
      stop();
      retireTimers.forEach(clearTimeout);
      document.removeEventListener('visibilitychange', resync);
      window.removeEventListener('pageshow', resync);
    },
  };
}

const instances = new Map();

function initRoot(root) {
  if (instances.has(root)) {
    return;
  }
  const instance = setupInstance(root);
  if (instance) {
    instances.set(root, instance);
  }
}

function initAll(scope) {
  (scope || document).querySelectorAll('[data-promotional-countdown]').forEach(initRoot);
}

let editorListenersBound = false;

function bindEditorListeners() {
  if (editorListenersBound) {
    return;
  }
  editorListenersBound = true;

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    instances.forEach((instance, root) => {
      if (!event.target.contains(root)) {
        return;
      }
      instance.teardown();
      instances.delete(root);
    });
  });
}

export default function PromotionalCountdown() {
  initAll();
  if (window.Shopify && window.Shopify.designMode) {
    bindEditorListeners();
  }
}
